/**
 * The kind of thing each audit event records, and how that kind is drawn.
 *
 * A programme's history mixes revisions, decisions, exports and publications.
 * Each kind carries its own icon, badge and tone, so an approval reads apart
 * from an edit without reading the label.
 *
 * @module components/shared/audit-event-categories
 */

import type { Tone } from "@/components/shared/status-tones";
import { AUDIT_EVENT_TYPES, type AuditEventType } from "@/server/audit-events";

export type AuditEventCategory =
  | "revision"
  | "workflow"
  | "review"
  | "approval"
  | "export"
  | "publication"
  | "team"
  | "module"
  | "editing"
  | "access"
  | "administration";

const LOGIN_ACCESS = Object.fromEntries(
  AUDIT_EVENT_TYPES.filter((action) => action.startsWith("login.")).map((action) => [
    action,
    "access",
  ]),
) as Record<Extract<AuditEventType, `login.${string}`>, "access">;

/** Exhaustive by type, so a new event type cannot reach a history uncategorised. */
export const AUDIT_EVENT_CATEGORIES: Record<AuditEventType, AuditEventCategory> = {
  ...LOGIN_ACCESS,
  "assessment.override-assessments-added": "revision",
  "assessment.override-assessments-removed": "revision",
  "assessment.override-created": "revision",
  "assessment.override-reverted": "revision",
  "assessment.override-updated": "revision",
  "assignment.created": "team",
  "edit-lease.acquired": "editing",
  "edit-lease.disconnected": "editing",
  "edit-lease.released": "editing",
  "final-sign-off.recorded": "approval",
  "impersonation.started": "access",
  "impersonation.stopped": "access",
  "invitation.created": "administration",
  "invitation.reissued": "administration",
  "module-owner.assigned": "team",
  "module.created": "module",
  "module.reviewed": "review",
  "module.saved": "module",
  "module.submitted": "review",
  "programme-responsibility.assigned": "team",
  "programme.amendment-opened": "revision",
  "programme.approval-candidate-created": "review",
  "programme.approved": "approval",
  "programme.change-case-approved": "approval",
  "programme.change-case-discarded": "revision",
  "programme.change-case-identity-impact-decided": "revision",
  "programme.change-case-internal-review-completed": "review",
  "programme.change-case-reviewed": "review",
  "programme.change-case-submitted": "review",
  "programme.change-case-updated": "revision",
  "programme.change-case-withdrawn": "revision",
  "programme.change-team-confirmed": "team",
  "programme.change-team-reset": "team",
  "programme.create": "revision",
  "programme.created": "revision",
  "programme.curriculum-migrated": "revision",
  "programme.delivery-status-changed": "workflow",
  "programme.end-of-life-set": "workflow",
  "programme.export": "export",
  "programme.export-previewed": "export",
  "programme.exported": "export",
  "programme.governance-finding-created": "review",
  "programme.governance-finding-resolved": "review",
  "programme.internal-review-skipped": "review",
  "programme.publication.published": "publication",
  "programme.publication.withdrawn": "publication",
  "programme.readiness-overridden": "workflow",
  "programme.replacement-created": "revision",
  "programme.review-recorded": "review",
  "programme.review-round-opened": "review",
  "programme.returned-to-draft": "workflow",
  "programme.revised": "revision",
  "programme.revised-from-snapshot": "revision",
  "programme.revision-applicability-created": "revision",
  "programme.signed-off": "approval",
  "programme.snapshot-created": "revision",
  "programme.state_changed": "workflow",
  "programme.updated": "revision",
  "programme.visibility-changed": "publication",
  "programme.workflow-transitioned": "workflow",
  "role.changed": "administration",
  "settings.access-policy.updated": "administration",
  "settings.provider.updated": "administration",
  "settings.updated": "administration",
  "snapshot.restored": "revision",
  "support-access.enabled": "access",
  "support-access.revoked": "access",
  "support-access.used": "access",
  "tenant-capabilities.changed": "administration",
  "tenant.created": "administration",
  "tenant.entitlement.decided": "administration",
  "user.activated": "administration",
  "user.deactivated": "administration",
  "user.email-changed": "administration",
  "user.invited": "administration",
  "workflow.transitioned": "workflow",
};

export interface AuditCategoryPresentation {
  label: string;
  icon: string;
  tone: Tone;
}

export const AUDIT_CATEGORY_PRESENTATION: Record<AuditEventCategory, AuditCategoryPresentation> = {
  revision: { label: "Revision", icon: "git-branch", tone: "info" },
  workflow: { label: "Workflow", icon: "flow-arrow", tone: "neutral" },
  review: { label: "Review", icon: "magnifying-glass", tone: "warning" },
  approval: { label: "Approval", icon: "seal-check", tone: "success" },
  export: { label: "Export", icon: "export", tone: "neutral" },
  publication: { label: "Publication", icon: "broadcast", tone: "info" },
  team: { label: "Team", icon: "users-three", tone: "neutral" },
  module: { label: "Module", icon: "squares-four", tone: "neutral" },
  editing: { label: "Editing session", icon: "lock-key", tone: "neutral" },
  access: { label: "Access", icon: "key", tone: "neutral" },
  administration: { label: "Administration", icon: "gear-six", tone: "neutral" },
};

/** An action's category; an action no list names yet reads as a revision. */
export function auditEventCategory(action: string): AuditEventCategory {
  return (
    (AUDIT_EVENT_CATEGORIES as Partial<Record<string, AuditEventCategory>>)[action] ?? "revision"
  );
}

/** Every action recorded under any of the categories, for a server filter. */
export function actionsInCategories(categories: readonly AuditEventCategory[]): AuditEventType[] {
  return AUDIT_EVENT_TYPES.filter((action) => categories.includes(AUDIT_EVENT_CATEGORIES[action]));
}
